import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Context } from '@grandvisionhq/carts-v2';
import { useLocation, useLink } from '@grandvisionhq/frk-routing';
import { Hooks } from '@grandvisionhq/fun-orders';
import { PaymentFlow, PaymentResult } from '@grandvisionhq/payments';
import { CreateOrderErrors, CheckoutErrorsMapping } from '../../mappers/errors-mapping.js';
import { dispatchCartChangeEvent } from '../carts/use-cart-change-listener.js';
import { CreateOrderError } from '../../models/orders/create-order-error.js';
import { appendQueryParamToUrl } from '../../utils/get-updated-url-with-query-params.js';

const useCreateOrder = ({ onError  } = {})=>{
    const [paymentFlow, setPaymentFlow] = React.useState(PaymentFlow.UNDEFINED);
    const [orderResponse, setOrderResponse] = React.useState(undefined);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { cart  } = Context.useCart();
    const { setContext  } = Context.useCartSettings();
    const { replace  } = useLocation();
    const { href: paymentHref  } = useLink({
        route: 'checkout.payment',
        params: {
            cartId: ''
        }
    }) || {};
    const { placeOrder  } = Hooks.usePlaceOrder();
    /**
   * @description  call createOrder endpoint with payload and handle response with redirection
   * @argument orderPayload request object of type CreateOrderRequest
   */ const createOrder = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderPayload) {
            setLoading(true);
            setOrderResponse(undefined);
            const paymentMethod = orderPayload.paymentRequest.paymentMethod;
            try {
                if (!paymentMethod) {
                    console.error('Need a payment method to create an order');
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED
                    });
                }
                if (!cart) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.CartNotFound
                    });
                }
                setError(null);
                const placeOrderResponse = yield placeOrder(swcHelpers.objectSpread({}, orderPayload));
                setOrderResponse(placeOrderResponse);
                if (placeOrderResponse && placeOrderResponse.redirect) {
                    if (placeOrderResponse.httpMethod !== 'GET') {
                        throw new CreateOrderError({
                            status: CreateOrderErrors.UNSUPPORTED_REDIRECT_METHOD
                        });
                    }
                    dispatchCartChangeEvent(orderPayload.checkoutId);
                    (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.redirectUrl) && replace(placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.redirectUrl);
                } else if (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.success) {
                    if (placeOrderResponse.resultType === 'QRCODE') {
                        setPaymentFlow(PaymentFlow.QR_CODE);
                    }
                    return swcHelpers.objectSpread({}, placeOrderResponse, {
                        cartId: orderPayload.checkoutId
                    });
                } else if (!(placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.success) && (placeOrderResponse === null || placeOrderResponse === void 0 ? void 0 : placeOrderResponse.cartId)) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.SomethingWentWrong,
                        cartHash: placeOrderResponse.cartHash,
                        cartId: placeOrderResponse.cartId,
                        type: orderPayload.paymentRequest.paymentMethod
                    });
                } else {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.SomethingWentWrong,
                        cartId: orderPayload.checkoutId,
                        type: orderPayload.paymentRequest.paymentMethod
                    });
                }
                setLoading(false);
                return placeOrderResponse;
            } catch (e) {
                console.error(`Order creation failed for ${orderPayload.checkoutId}`);
                setLoading(false);
                if (e instanceof CreateOrderError) {
                    onError && onError(e.status, e === null || e === void 0 ? void 0 : e.label);
                }
                setError(CreateOrderErrors.ORDER_CREATION_CALL_FAILED);
                if (e instanceof CreateOrderError) {
                    var ref;
                    if ((e === null || e === void 0 ? void 0 : e.cartHash) && (e === null || e === void 0 ? void 0 : e.cartId)) {
                        setContext && setContext({
                            cartId: e === null || e === void 0 ? void 0 : e.cartId,
                            cartHash: e === null || e === void 0 ? void 0 : e.cartHash
                        });
                    }
                    paymentHref && replace(appendQueryParamToUrl(paymentHref, {
                        cartHash: e === null || e === void 0 ? void 0 : e.cartHash,
                        cartId: e === null || e === void 0 ? void 0 : e.cartId,
                        type: e === null || e === void 0 ? void 0 : (ref = e.type) === null || ref === void 0 ? void 0 : ref.toUpperCase(),
                        status: PaymentResult.Failed
                    }));
                }
                return {
                    success: false,
                    redirect: false
                };
            }
        });
        return function(orderPayload) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart,
        onError,
        paymentHref,
        placeOrder,
        replace,
        setContext,
        setPaymentFlow
    ]);
    return React.useMemo(()=>[
            createOrder,
            {
                loading,
                error,
                orderResponse,
                paymentFlow,
                setPaymentFlow
            }, 
        ]
    , [
        createOrder,
        loading,
        error,
        orderResponse,
        paymentFlow,
        setPaymentFlow
    ]);
};

export { useCreateOrder };
//# sourceMappingURL=use-create-order.js.map
